const DOC_TYPE_PREFIXES: Record<string, string> = {
  quote: 'DEV',
  order: 'CMD',
  delivery: 'BL',
  invoice: 'FAC',
  credit_note: 'AV',
  purchase_order: 'CDF',
  reception: 'BR',
  purchase_invoice: 'FAA',
  payment: 'PAI',
  inventory: 'INV',
};

/**
 * Génère un numéro de document au format légal tunisien.
 * Format : {PREFIX}-{YEAR}-{NUMBER}
 * Exemple : FAC-2026-0001
 *
 * Note : la séquence atomique (nextval) est gérée côté base de données.
 * Cette fonction ne fait que formater.
 */
export function formatDocumentNumber(
  docType: string,
  sequenceValue: number,
  year?: number,
): string {
  const prefix = getDocTypePrefix(docType);
  const y = year ?? new Date().getFullYear();
  const num = String(sequenceValue).padStart(4, '0');
  return `${prefix}-${y}-${num}`;
}

export function getDocTypePrefix(docType: string): string {
  return DOC_TYPE_PREFIXES[docType] ?? docType.toUpperCase();
}

/**
 * Construit un numéro de document à partir d'un préfixe, d'une année et d'une
 * longueur (padding) configurables. Format légal tunisien : {PREFIX}-{YEAR}-{NNNN}.
 * Exemple : buildDocumentNumber('FAC', 2026, 1, 4) → 'FAC-2026-0001'.
 *
 * `year: null` = numérotation continue sans année ({PREFIX}-{NNNN}) — utilisée
 * par les tenants qui reprennent la numérotation historique de leur ancien
 * système (company_settings.doc_number_include_year = false).
 * Exemple : buildDocumentNumber('FAC', null, 2600148, 7) → 'FAC-2600148'.
 *
 * Contrairement à formatDocumentNumber(), le préfixe n'est pas dérivé d'une table
 * codée en dur : il provient de la configuration du tenant (company_settings).
 * Le compteur séquentiel atomique reste géré côté backend ; cette fonction ne
 * fait que formater.
 */
export function buildDocumentNumber(
  prefix: string,
  year: number | null,
  value: number,
  padding = 4,
): string {
  const safePadding = Math.max(1, Math.trunc(padding) || 1);
  const num = String(value).padStart(safePadding, '0');
  return year == null ? `${prefix}-${num}` : `${prefix}-${year}-${num}`;
}
